import React, { useEffect, useState } from "react";
import { ATTENDANCE_REPORT_ZM } from "../../constants";
import dayjs from "dayjs";
import axios from "axios";
import { ArrowRight, Calendar, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";

const TeamTravelReportComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [attendanceData, setAttendanceData] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem("selectedDate") ||
      dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );

  const navigate = useNavigate();

  const fetchReportData = async (date) => {
    try {
      setLoading(true);
      const formattedStartDate = dayjs(date).format("YYYY-MM-DD");

      const response = await axios.get(
        `${ATTENDANCE_REPORT_ZM}?employeeId=${localStorage.getItem(
          "username"
        )}&startDate=${formattedStartDate}&endDate=${formattedStartDate}`
      );
      setAttendanceData(response.data.regions);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(selectedDate);
  }, []);

  // Process data to consolidate BDMs that appear in multiple regions
  const consolidatedBdms = [];
  const processedBdmIds = new Set();

  attendanceData.forEach((region) => {
    region.bdms.forEach((bdm) => {
      if (!processedBdmIds.has(bdm.EmployeeId)) {
        // This is the first time we're seeing this BDM
        processedBdmIds.add(bdm.EmployeeId);

        // Find all regions this BDM is part of
        const bdmRegions = attendanceData
          .filter((r) => r.bdms.some((b) => b.EmployeeId === bdm.EmployeeId))
          .map((r) => r.RegionName);

        consolidatedBdms.push({
          ...bdm,
          regions: bdmRegions,
        });
      }
    });
  });

  return (
    <>
      {isMobile ? (
        <>
          <div className="flex flex-col h-screen bg-gray-50">
            {/* Header - Responsive */}
            <div className="p-3 md:p-4 shadow-md bg-white flex justify-between items-center">
              <h1 className="text-lg md:text-xl font-bold">
                Team Travel Report
              </h1>
              <button
                onClick={() => setShowFilter((prev) => !prev)}
                className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
              >
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>
            {showFilter && (
              <div className="bg-white p-4 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-500" />
                  <span className="text-sm font-medium">Date Range:</span>
                </div>
                <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border p-2 rounded text-sm w-full sm:w-auto"
                  />
                  <span className="text-sm">to</span>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border p-2 rounded text-sm w-full sm:w-auto"
                  />
                </div>
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 w-full sm:w-auto"
                  onClick={() => {
                    fetchReportData(selectedDate);
                    setShowFilter(false);
                  }}
                >
                  Apply
                </button>
              </div>
            )}

            {/* Main Table - with responsive handling */}
            <div className="flex-1 p-2 md:p-4 overflow-auto">
              <div className="bg-white rounded shadow-sm overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        BDM Name
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Region(s)
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        In
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Out
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {consolidatedBdms.map((bdm, index) => (
                      <tr
                        key={bdm.EmployeeId}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div>{bdm.EmployeeName}</div>
                          <div className="sm:hidden text-xs text-gray-500 mt-1">
                            {bdm.regions.join(", ")}
                          </div>
                          <div className="md:hidden text-xs text-gray-500 mt-1">
                            {bdm.attendanceIn
                              ? `In: ${bdm.attendanceIn}`
                              : null}
                            {bdm.attendanceIn && bdm.attendanceOut
                              ? " • "
                              : null}
                            {bdm.attendanceOut
                              ? `Out: ${bdm.attendanceOut}`
                              : null}
                            {!bdm.attendanceIn && !bdm.attendanceOut
                              ? "No attendance"
                              : null}
                          </div>
                        </td>
                        <td className="px-3 md:px-6 py-3 md:py-4 text-sm text-gray-900 hidden sm:table-cell">
                          {bdm.regions.join(", ")}
                        </td>
                        <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                          {bdm.attendanceIn || "—"}
                        </td>
                        <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                          {bdm.attendanceOut || "—"}
                        </td>
                        <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => {
                              navigate("/attendanceReport/detailszm", {
                                state: {
                                  prevFilters: selectedDate,
                                  edate: selectedDate,
                                  bdmId: bdm?.EmployeeId,
                                },
                              });
                            }}
                            className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                          >
                            <span className="md:block">View</span>
                            <span className="hidden md:inline"> Details</span>
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white p-2 md:p-3 text-xs text-gray-500 border-t text-center">
              Project Parivartan • Date Range: {selectedDate} to {selectedDate}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col h-screen bg-gray-50">
          {/* Header */}
          <div className="p-4 shadow-md flex items-center justify-between bg-white">
            <h1 className="text-xl font-bold">Team Travel Report</h1>
            <button
              onClick={() => setShowFilter((prev) => !prev)}
              className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>

          {/* Date Filter */}
          {showFilter && (
            <div className="bg-white p-4 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 shadow-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-500" />
                <span className="text-sm font-medium">Date Range:</span>
              </div>
              <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border p-2 rounded text-sm w-full sm:w-auto"
                />
                <span className="text-sm">to</span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border p-2 rounded text-sm w-full sm:w-auto"
                />
              </div>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 w-full sm:w-auto"
                onClick={() => fetchReportData(selectedDate)}
              >
                Apply
              </button>
            </div>
          )}

          {/* Main Table */}
          <div className="flex-1 p-4 overflow-auto">
            <div className="bg-white rounded shadow-sm min-w-[640px] sm:min-w-full overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      BDM Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Region(s)
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Attendance In
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Attendance Out
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {consolidatedBdms.map((bdm, index) => (
                    <tr
                      key={bdm.EmployeeId}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                        {bdm.EmployeeName}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {bdm.regions.join(", ")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {bdm.attendanceIn || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {bdm.attendanceOut || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap space-y-1">
                        <div>
                          
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              navigate("/attendanceReport/detailszm", {
                                state: {
                                  prevFilters: selectedDate,
                                  edate: selectedDate,
                                  bdmId: bdm?.EmployeeId,
                                  reportType: "detail",
                                },
                              });
                            }}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                          >
                            View Details <ArrowRight className="h-3 w-3 ml-1" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white p-3 text-xs text-gray-500 border-t text-center">
            Project Parivartan • Date Range: May 7-8, 2025
          </div>
        </div>
      )}
    </>
  );
};

export default TeamTravelReportComponent;
